import React from 'react';
import { Field, Form, FormElement, FormRenderProps } from '@progress/kendo-react-form';
import { Button } from '@progress/kendo-react-buttons';
import CpsLoader from '../../common/CpsLoader';
import { LoadingGridPanel } from '../../grid/LoadingGridPanel';
import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { useGlobalizationContext, useTranslation, useUiAdminConfigsContext } from '@cs-cms/state-management';
import {
  CpsMessageBarState,
  IdNamePair,
  CpsMessageBarType,
  CPS_UI_ADMIN_CONFIG_TYPE,
  CpsConfirmDialogState,
  CPS_CONFIRM_DIALOG_STATE
} from '@cs-cms/types';
import { FormDropDownList } from '../../form/fields/FormDropdown';
import { FormTextArea } from '../../form/fields/FormMultilineText';
import { FormSwitcher } from '../../form/fields/FormSwitcher';
import { FormTextBox } from '../../form/fields/FormTextBox';

interface MessageBarDialogState {
  visible: boolean;
  values?: Record<string, any>;
}

export const MessageBarManagement: React.FC = () => {
  const [initialValues, setInitValues] = React.useState<Partial<CpsMessageBarState>>();
  const [dialogState, setDialogState] = React.useState<MessageBarDialogState>({ visible: false });
  const isInitialized = React.useRef(false);
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const messageBarTypes: IdNamePair[] = [
    {
      id: CpsMessageBarType.INFO,
      name: translate('custom.settings.adminCenter.messageBar.type.info')
    },
    {
      id: CpsMessageBarType.ERROR,
      name: translate('custom.settings.adminCenter.messageBar.type.error')
    },
    {
      id: CpsMessageBarType.BLOCKED,
      name: translate('custom.settings.adminCenter.messageBar.type.blocked')
    },
    {
      id: CpsMessageBarType.SEVERE_WARNING,
      name: translate('custom.settings.adminCenter.messageBar.type.severeWarning')
    },
    {
      id: CpsMessageBarType.SUCCESS,
      name: translate('custom.settings.adminCenter.messageBar.type.success')
    },
    {
      id: CpsMessageBarType.WARNING,
      name: translate('custom.settings.adminCenter.messageBar.type.warning')
    }
  ];

  const {
    isLoading,
    uiAdminConfigState,
    upsertUiAdminConfigEntry,
    uiAdminConfigPreviewState,
    setUiAdminConfigPreviewState
  } = useUiAdminConfigsContext();

  React.useEffect(() => {
    if (isLoading || isInitialized.current) {
      return;
    }

    if (uiAdminConfigState.messageBar) {
      const values: CpsMessageBarState = uiAdminConfigState.messageBar as CpsMessageBarState;
      setInitValues(values);
    } else {
      setInitValues({});
    }

    isInitialized.current = true;
  }, [isLoading, uiAdminConfigState.messageBar]);

  const upsertMessageBar = (values: Record<string, any>) => {
    const body = JSON.stringify(values);
    upsertUiAdminConfigEntry(CPS_UI_ADMIN_CONFIG_TYPE.MESSAGE_BAR, body);

    if (setUiAdminConfigPreviewState && uiAdminConfigPreviewState?.messageBar && values.enabled) {
      setUiAdminConfigPreviewState({
        ...uiAdminConfigPreviewState,
        [CPS_UI_ADMIN_CONFIG_TYPE.MESSAGE_BAR]: undefined
      });
    }
  };

  const handleSubmit = (values: Record<string, any>) => {
    if (values.enabled) {
      setDialogState({ visible: true, values });
    } else {
      upsertMessageBar(values);
    }
  };

  const handlePreview = (formRenderProps: FormRenderProps) => {
    if (setUiAdminConfigPreviewState) {
      const messageBarPreviewState: CpsMessageBarState = {
        enabled: true,
        messageContent: formRenderProps.valueGetter('messageContent'),
        messageBarType: formRenderProps.valueGetter('messageBarType'),
        truncated: formRenderProps.valueGetter('truncated'),
        hyperlinkText: formRenderProps.valueGetter('hyperlinkText'),
        hyperlinkAddress: formRenderProps.valueGetter('hyperlinkAddress'),
        isPreview: true
      };
      setUiAdminConfigPreviewState({
        ...uiAdminConfigPreviewState,
        [CPS_UI_ADMIN_CONFIG_TYPE.MESSAGE_BAR]: messageBarPreviewState
      });
    }
  };

  const handleDialogConfirm = (confirmed: CpsConfirmDialogState) => {
    if (confirmed === CPS_CONFIRM_DIALOG_STATE.YES && dialogState.values) {
      upsertMessageBar(dialogState.values);
    }
    setDialogState({ visible: false });
  };

  if (!initialValues) {
    return <CpsLoader />;
  }

  return (
    <div>
      {isLoading && <LoadingGridPanel />}
      <Form
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={(formRenderProps: FormRenderProps) => {
          return (
            <FormElement>
              <Field name={'enabled'} component={FormSwitcher} label={translate('custom.common.enable')} />
              <Field
                name={'messageBarType'}
                component={FormDropDownList}
                label={translate('custom.settings.adminCenter.messageBar.type.title')}
                data={messageBarTypes}
                textField={'name'}
                dataItemKey={'id'}
                dataUsesTranslations={true}
                onChange={(event: DropDownListChangeEvent) => {
                  formRenderProps.onChange('messageBarType', { value: event.value });
                }}
              />
              <Field
                name={'truncated'}
                component={FormSwitcher}
                label={translate('custom.settings.adminCenter.messageBar.truncated')}
              />
              <h3>{translate('custom.settings.adminCenter.messageBar.messageContent')}</h3>
              <Field name={'messageContent'} component={FormTextArea} />
              <h3>{translate('custom.settings.adminCenter.messageBar.hyperlink.title')}</h3>
              <Field
                label={translate('custom.settings.adminCenter.messageBar.hyperlink.textToDisplay')}
                name={'hyperlinkText'}
                component={FormTextBox}
              />
              <Field
                label={translate('custom.settings.adminCenter.messageBar.hyperlink.address')}
                name={'hyperlinkAddress'}
                component={FormTextBox}
              />
              <div className={'cpsFormButtons'}>
                <Button
                  type={'button'}
                  themeColor={'secondary'}
                  fillMode="solid"
                  onClick={() => handlePreview(formRenderProps)}
                >
                  {translate('custom.common.preview')}
                </Button>
                <Button themeColor="dark" disabled={!formRenderProps.modified}>
                  {translate('custom.common.save')}
                </Button>
              </div>
            </FormElement>
          );
        }}
      />
      {dialogState.visible && (
        <ConfirmDialog
          message={translate('custom.settings.adminCenter.messageBar.confirmationMessage')}
          onConfirm={handleDialogConfirm}
        />
      )}
    </div>
  );
};
